import React from 'react';
import ShownPanelsEnum from '../../../Enums/ShownPanelsEnum';
import ButtonColorsEnum from '../../../Enums/ButtonColorsEnum';
import ButtonRow from '../../../Elements/ButtonRow';
import GeneralEditWizzardBase from '../../../Elements/GeneralEditWizzard/GeneralEditWizzardBaseFigma';
import ErrorMessage from '../../../Elements/ErrorMessage';
import PlanningDetailMultiEditWindowDescription from './PlanningDetailMultiEditWindowDescription';
import PlanningDetailStatusUpdateWindowDescription from './PlanningDetailStatusUpdateWindowDescription';
import ContractedOverrideWindowDescription from './ContractedOverrideWindowDescription';
import ResetContractedOverrideWindowDescription from './ResetContractedOverrideWindowDescription';
import UnlockDatesWindowDescription from './UnlockDatesWindowDescription';
import LockDatesWindowDescription from './LockDatesWindowDescription';
import RolloverWindowDescription from './RolloverWindowDescription';
import ContractedOverrideItem from '../../../Domain/ContractedOverrideItem';
import CrudTypesEnum from '../../../Enums/CrudTypesEnum';
import PlanningExportWindowDescription from '../Export/PlanningExportWindowDescription';
import PlanningExportItem from '../../../Domain/PlanningExportItem';
import PlanningExportLevelsEnum from '../../../Enums/PlanningExportLevelsEnum';
import PlanningStatusEnum from '../../../Enums/PlanningStatusEnum';

const BUTTONS = [
  {
    label: 'MULTI EDIT',
    key: ShownPanelsEnum.MultiEdit,
    icon: 'fa-solid fa-edit',
    color: ButtonColorsEnum.Secondary,
    roleKey: '',
  },
  {
    label: 'STATUS UPDATE',
    key: ShownPanelsEnum.MassStatusUpdate,
    icon: 'fa-solid fa-pen-alt',
    color: ButtonColorsEnum.Secondary,
    roleKey: '',
  },
  {
    label: 'OVERRIDE CONTRACTED',
    key: ShownPanelsEnum.ContractedOverride,
    icon: 'fa-solid fa-pen-ruler',
    color: ButtonColorsEnum.Secondary,
    roleKey: 'planning_contracted_override',
  },
  {
    label: 'RESET OVERRIDE',
    key: ShownPanelsEnum.ContractedReset,
    icon: 'fa-solid fa-undo',
    color: ButtonColorsEnum.Secondary,
    roleKey: 'planning_contracted_reset',
  },
  {
    label: 'UNLOCK DATES',
    key: ShownPanelsEnum.DateUnlock,
    icon: 'fa-solid fa-lock-open',
    color: ButtonColorsEnum.Secondary,
    roleKey: 'planning_date_unlock',
  },
  {
    label: 'LOCK DATES',
    key: ShownPanelsEnum.DateLock,
    icon: 'fa-solid fa-lock',
    color: ButtonColorsEnum.Secondary,
    roleKey: 'planning_date_lock',
  },
  {
    label: 'ROLLOVER',
    key: ShownPanelsEnum.Rollover,
    icon: 'fa-solid fa-rotate-right',
    color: ButtonColorsEnum.Secondary,
    roleKey: 'planning_rollover',
  },
  {
    label: 'EXPORT',
    key: ShownPanelsEnum.Export,
    icon: 'fa-solid fa-file-excel',
    color: ButtonColorsEnum.Secondary,
    roleKey: '',
  },
];

function PlanningDetailMassActions({
  shownPanel,
  showPanel,
  closePanel,
  refreshData,
  selectedItems,
  maxPlannedAssignments,
}) {
  return (
    <>
      <ButtonRow
        labels={BUTTONS.map((b) => b.label)}
        keys={BUTTONS.map((b) => b.key)}
        icons={BUTTONS.map((b) => b.icon)}
        colors={BUTTONS.map((b) => b.color)}
        roleKeys={BUTTONS.map((b) => b.roleKey)}
        buttonClassNames={BUTTONS.map(() => 'figmabutton_autowidth')}
        disabled={BUTTONS.map((b) => b.key === ShownPanelsEnum.Export && selectedItems.length === 0)}
        value={shownPanel}
        onClick={(key) => {
          if (
            (key === ShownPanelsEnum.MultiEdit ||
              key === ShownPanelsEnum.MassStatusUpdate ||
              key === ShownPanelsEnum.ContractedOverride ||
              key === ShownPanelsEnum.ContractedReset) &&
            selectedItems.length === 0
          ) {
            ErrorMessage.SetNotification('Please select at least one item.');
            return;
          }
          // A Closed or Cancelled period is read-only, and an even distribution spreads the new total
          // across the whole selection, so the panel refuses to open rather than skip members of it.
          if (key === ShownPanelsEnum.ContractedOverride || key === ShownPanelsEnum.ContractedReset) {
            const readOnlyPeriods = selectedItems.filter(
              (item) => item.Status === PlanningStatusEnum.Closed || item.Status === PlanningStatusEnum.Cancelled
            );
            if (readOnlyPeriods.length > 0) {
              ErrorMessage.SetNotification(
                'The contracted volume of a Closed or Cancelled fieldwork period cannot be changed. Take these out of the selection: ' +
                  readOnlyPeriods.map((item) => `FW ${item.FieldworkOrderNumber}`).join(', ') +
                  '.'
              );
              return;
            }
          }
          // Rollover moves volume out of one Closed period into later ones, so the selection has to
          // contain both. Checking here keeps the panel from opening onto a choice that can only fail.
          if (key === ShownPanelsEnum.Rollover) {
            const closedCount = selectedItems.filter((item) => item.Status === PlanningStatusEnum.Closed).length;
            if (closedCount === 0) {
              ErrorMessage.SetNotification(
                'Select the Closed fieldwork period you want to roll over, together with the later periods to roll it into.'
              );
              return;
            }
            if (selectedItems.length < 2) {
              ErrorMessage.SetNotification(
                'Select at least one more fieldwork period to roll the volume into, besides the Closed one.'
              );
              return;
            }
          }
          showPanel(key);
        }}
      />
      {shownPanel === ShownPanelsEnum.MassStatusUpdate ? (
        <GeneralEditWizzardBase
          crudType={CrudTypesEnum.Create}
          editedItem={new PlanningDetailStatusUpdateWindowDescription.itemType(selectedItems)}
          clickOnCancel={closePanel}
          clickOnSave={() => {
            closePanel();
            refreshData();
          }}
          windowDescription={PlanningDetailStatusUpdateWindowDescription}
          approveBeforeSaveCheck={PlanningDetailStatusUpdateWindowDescription.approveBeforeSaveCheck}
          approveBeforeSaveMessage={PlanningDetailStatusUpdateWindowDescription.approveBeforeSaveMessage}
          errorCode={'PDL251'}
        />
      ) : null}
      {shownPanel === ShownPanelsEnum.MultiEdit ? (
        <GeneralEditWizzardBase
          crudType={CrudTypesEnum.Create}
          editedItem={new PlanningDetailMultiEditWindowDescription.itemType(selectedItems, maxPlannedAssignments)}
          clickOnCancel={closePanel}
          clickOnSave={() => {
            closePanel();
            refreshData();
          }}
          windowDescription={PlanningDetailMultiEditWindowDescription}
          errorCode={'PDL233'}
        />
      ) : null}
      {shownPanel === ShownPanelsEnum.ContractedOverride ? (
        <GeneralEditWizzardBase
          crudType={CrudTypesEnum.Create}
          editedItem={new ContractedOverrideItem(selectedItems)}
          clickOnCancel={closePanel}
          clickOnSave={() => {
            closePanel();
            refreshData();
          }}
          windowDescription={ContractedOverrideWindowDescription}
          errorCode={'PDL_OVERRIDE'}
        />
      ) : null}
      {shownPanel === ShownPanelsEnum.ContractedReset ? (
        <GeneralEditWizzardBase
          crudType={CrudTypesEnum.Create}
          editedItem={new ResetContractedOverrideWindowDescription.itemType(selectedItems)}
          clickOnCancel={closePanel}
          clickOnSave={() => {
            closePanel();
            refreshData();
          }}
          windowDescription={ResetContractedOverrideWindowDescription}
          errorCode={'PDL_RESET'}
        />
      ) : null}
      {shownPanel === ShownPanelsEnum.DateUnlock ? (
        <GeneralEditWizzardBase
          crudType={CrudTypesEnum.Create}
          editedItem={new UnlockDatesWindowDescription.itemType(selectedItems)}
          clickOnCancel={closePanel}
          clickOnSave={() => {
            closePanel();
            refreshData();
          }}
          windowDescription={UnlockDatesWindowDescription}
          errorCode={'PDL_UNLOCK'}
        />
      ) : null}
      {shownPanel === ShownPanelsEnum.DateLock ? (
        <GeneralEditWizzardBase
          crudType={CrudTypesEnum.Create}
          editedItem={new LockDatesWindowDescription.itemType(selectedItems)}
          clickOnCancel={closePanel}
          clickOnSave={() => {
            closePanel();
            refreshData();
          }}
          windowDescription={LockDatesWindowDescription}
          errorCode={'PDL_LOCK'}
        />
      ) : null}
      {shownPanel === ShownPanelsEnum.Rollover ? (
        <GeneralEditWizzardBase
          crudType={CrudTypesEnum.Create}
          editedItem={new RolloverWindowDescription.itemType(selectedItems)}
          clickOnCancel={closePanel}
          clickOnSave={() => {
            closePanel();
            refreshData();
          }}
          windowDescription={RolloverWindowDescription}
          errorCode={'PDL_ROLLOVER'}
        />
      ) : null}
      {shownPanel === ShownPanelsEnum.Export ? (
        <GeneralEditWizzardBase
          crudType={CrudTypesEnum.Create}
          editedItem={
            new PlanningExportItem({
              Level: PlanningExportLevelsEnum.Detail,
              SelectedIds: selectedItems.map((x) => x.PlanningDetailId),
            })
          }
          clickOnCancel={closePanel}
          clickOnSave={closePanel}
          windowDescription={PlanningExportWindowDescription.forLevel(PlanningExportLevelsEnum.Detail)}
          errorCode={'PDL_EXPORT'}
        />
      ) : null}
    </>
  );
}

export default PlanningDetailMassActions;
